// Picking up ATLAS claim receipts from a folder on Emma's computer
// (Documents\ATLAS Claim Receipts, 26 Oct 2026) for the Claim receipts folder.
//
// A web page cannot read a folder by itself. Chrome's File System Access API lets a page
// keep a folder the person chose, and with "Allow on every visit" read it again each time
// the page opens. Two ways in, because the portal is used two ways:
//
//   - opened directly (portal.completelawsupport.com): the page holds the folder itself;
//   - inside completelawsupport.com, which shows the portal in a frame: Chrome refuses a
//     folder picker in a frame from another site, so the outer page does the folder work
//     and hands the PDFs in (ejack923/new-website, src/lib/receiptFolderBridge.js). It
//     answers only this portal, and only with the PDFs in the folder it was given.
//
// Kept in the browser: the folder itself (a handle, not its contents) and a one-way
// fingerprint of each file already dealt with, so it is not offered again. Nothing about a
// client is stored - the receipts themselves go to the portal's private storage when saved.

export const BRIDGE_TYPE = "cls-receipt-folder";
export const WEBSITE_ORIGIN = "https://completelawsupport.com";
const DB_NAME = "cls-claim-receipt-folder";
const STORE = "kv";
const REPLY_TIMEOUT_MS = 2500;
// Linking and checking wait on Emma (the picker, the permission prompt), not on a reply.
const INTERACTIVE_TIMEOUT_MS = 120000;

const inFrame = () => {
  try { return window.self !== window.top; } catch { return true; }
};
const hasPicker = () => typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";

// ---- A little IndexedDB key/value store ------------------------------------------------

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") { reject(new Error("This browser cannot keep a folder.")); return; }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function idb(mode, fn) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const request = fn(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(request?.result);
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}
const idbGet = (key) => idb("readonly", (store) => store.get(key));
const idbSet = (key, value) => idb("readwrite", (store) => store.put(value, key));
const idbDelete = (key) => idb("readwrite", (store) => store.delete(key));

// ---- Fingerprints of files already dealt with -------------------------------------------

export async function fingerprintOf(file) {
  const bytes = new TextEncoder().encode(`${file.name}|${file.size}|${file.lastModified}`);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function seenFingerprints(brand) {
  try { return new Set((await idbGet(`seen:${brand}`)) || []); } catch { return new Set(); }
}

export async function markSeen(brand, fingerprints = []) {
  if (!fingerprints.length) return;
  try {
    const seen = await seenFingerprints(brand);
    for (const fingerprint of fingerprints) seen.add(fingerprint);
    // The newest few thousand are plenty; a receipt is uploaded within days of claiming.
    await idbSet(`seen:${brand}`, [...seen].slice(-5000));
  } catch (error) {
    console.warn("Could not remember the receipts already dealt with:", error);
  }
}

// ---- Reading a folder (used here when opened directly, and by the website's bridge) -------

// PDFs in the folder and one level of folders inside it.
export async function listFolderPdfs(handle) {
  const files = [];
  const walk = async (dir, depth) => {
    for await (const entry of dir.values()) {
      if (entry.kind === "file" && /\.pdf$/i.test(entry.name)) files.push(await entry.getFile());
      else if (entry.kind === "directory" && depth < 1) await walk(entry, depth + 1);
    }
  };
  await walk(handle, 0);
  return files;
}

const directSource = {
  mode: "direct",
  async status(brand) {
    const handle = await idbGet(`folder:${brand}`).catch(() => null);
    if (!handle) return { linked: false };
    const permission = await handle.queryPermission({ mode: "read" }).catch(() => "prompt");
    return { linked: true, name: handle.name, permission };
  },
  async link(brand) {
    const handle = await window.showDirectoryPicker({ id: "atlas-claim-receipts", mode: "read", startIn: "documents" });
    await idbSet(`folder:${brand}`, handle);
    return { linked: true, name: handle.name, permission: "granted" };
  },
  async files(brand, { interactive = false } = {}) {
    const handle = await idbGet(`folder:${brand}`);
    if (!handle) throw new Error("No folder is linked.");
    let permission = await handle.queryPermission({ mode: "read" });
    if (permission !== "granted" && interactive) permission = await handle.requestPermission({ mode: "read" });
    if (permission !== "granted") return { permission, files: [] };
    return { permission, files: await listFolderPdfs(handle) };
  },
  async unlink(brand) {
    await idbDelete(`folder:${brand}`);
    return { linked: false };
  },
};

// ---- Asking completelawsupport.com to do it -----------------------------------------------

function ask(op, payload = {}, timeoutMs = REPLY_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    const requestId = `${op}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const done = (handler) => {
      clearTimeout(timer);
      window.removeEventListener("message", listen);
      handler();
    };
    const listen = (event) => {
      if (event.origin !== WEBSITE_ORIGIN || event.source !== window.parent) return;
      const data = event.data;
      if (!data || data.type !== `${BRIDGE_TYPE}:reply` || data.requestId !== requestId) return;
      done(() => (data.ok ? resolve(data) : reject(new Error(data.error || "The folder could not be read."))));
    };
    const timer = setTimeout(() => done(() => reject(Object.assign(new Error("No reply from the website."), { code: "no-bridge" }))), timeoutMs);
    window.addEventListener("message", listen);
    window.parent.postMessage({ type: BRIDGE_TYPE, op, requestId, ...payload }, WEBSITE_ORIGIN);
  });
}

const bridgeSource = {
  mode: "website",
  status: (brand) => ask("status", { brand }),
  link: (brand) => ask("link", { brand }, INTERACTIVE_TIMEOUT_MS),
  files: (brand, { interactive = false } = {}) => ask("files", { brand, interactive }, interactive ? INTERACTIVE_TIMEOUT_MS : 30000),
  unlink: (brand) => ask("unlink", { brand }),
};

// Which way in works here: "direct", "website", or null with the reason.
export async function folderSource() {
  if (!inFrame()) return hasPicker() ? { source: directSource } : { source: null, reason: "browser" };
  try {
    await ask("status", { brand: "_probe" });
    return { source: bridgeSource };
  } catch {
    return { source: null, reason: "frame" };
  }
}

// The files in the linked folder that have not been dealt with, with their fingerprints.
export async function newFolderFiles(source, brand, { interactive = false } = {}) {
  const { permission, files } = await source.files(brand, { interactive });
  const seen = await seenFingerprints(brand);
  const fresh = [];
  for (const file of files || []) {
    const fingerprint = await fingerprintOf(file);
    if (!seen.has(fingerprint)) fresh.push({ file, fingerprint });
  }
  return { permission, files: fresh };
}

// The receipt's "Service Provider", so a folder holding several firms' receipts gives each
// firm's page only its own. A receipt is only left out when it clearly names another firm;
// a provider name not recognised still comes through, to be checked by the person.
const FIRM_PROVIDERS = {
  vals: /aboriginal legal service/i,
  lacw: /law (?:and|&) advocacy|centre for women/i,
  rga: /greensill/i,
};
export function receiptIsForFirm(serviceProvider = "", brand = "") {
  const provider = String(serviceProvider || "");
  const owner = Object.keys(FIRM_PROVIDERS).find((firm) => FIRM_PROVIDERS[firm].test(provider));
  return !owner || owner === brand;
}
